<template>
  <div class="create-post">
    <div class="post-input">
      <textarea 
        v-model="postContent"
        placeholder="¿wa?"
        class="content-textarea"
        maxlength="500"
        @input="updateCharCount"
      ></textarea>
      
      <div class="char-counter" :class="{ 'near-limit': charCount > 450, 'at-limit': charCount >= 500 }">
        {{ charCount }}/500
      </div>
    </div>

    <div class="media-section">
      <div class="media-controls">
        <div class="file-inputs">
          <label class="file-input-label">
            <i class="pi pi-image"></i>
            Imágenes
            <input 
              type="file" 
              ref="imageInput"
              multiple 
              accept="image/*"
              @change="handleImageSelect"
              style="display: none;"
            />
          </label>
          
          <label class="file-input-label">
            <i class="pi pi-video"></i>
            Videos
            <input 
              type="file" 
              ref="videoInput"
              multiple 
              accept="video/*"
              @change="handleVideoSelect"
              style="display: none;"
            />
          </label>
        </div>
        
        <div class="file-counter" v-if="totalFiles > 0">
          {{ totalFiles }}/4 archivos
        </div>
      </div>

      <div class="selected-files" v-if="selectedImages.length > 0 || selectedVideos.length > 0">
        <div class="file-previews">
          <div 
            v-for="(image, index) in selectedImages" 
            :key="'img-' + index"
            class="file-preview"
          >
            <img :src="getFilePreview(image)" alt="Preview" />
            <button @click="removeImage(index)" class="remove-file">×</button>
            <span class="file-name">{{ image.name }}</span>
          </div>
          
          <div 
            v-for="(video, index) in selectedVideos" 
            :key="'vid-' + index"
            class="file-preview video-preview"
          >
            <video :src="getFilePreview(video)" muted></video>
            <button @click="removeVideo(index)" class="remove-file">×</button>
            <span class="file-name">{{ video.name }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="post-actions">
      <Button 
        @click="submitPost" 
        label="Publicar" 
        class="btn-post"
        :disabled="!canPost || isPosting"
        :loading="isPosting"
      />
      
      <span v-if="errorMessage" class="error-message">{{ errorMessage }}</span>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import { postService } from '@/services/apiService';
import Button from 'primevue/button';

export default {
  name: 'CreatePost',
  props: {
    postPadreId: {
      type: [String, Number],
      default: null
    }
  },
  components: {
    Button
  },
  emits: ['postCreated'],
  setup(props, { emit }) {
    const postContent = ref('');
    const selectedImages = ref([]);
    const selectedVideos = ref([]);
    const isPosting = ref(false);
    const errorMessage = ref('');
    const imageInput = ref(null);
    const videoInput = ref(null);

    const charCount = computed(() => postContent.value.length);
    const totalFiles = computed(() => selectedImages.value.length + selectedVideos.value.length);
    
    const canPost = computed(() => {
      // Puede publicar si tiene contenido de texto O archivos multimedia
      const hasContent = postContent.value.trim().length > 0;
      const hasMedia = totalFiles.value > 0;
      return (hasContent || hasMedia) && totalFiles.value <= 4 && !isPosting.value;
    });

    const updateCharCount = () => {
      // El charCount se actualiza automáticamente por el computed
    };

    const handleImageSelect = (event) => {
      const files = Array.from(event.target.files);
      const availableSlots = 4 - totalFiles.value;
      
      if (files.length > availableSlots) {
        errorMessage.value = `Solo puedes agregar ${availableSlots} archivos más (máximo 4 total)`;
        return;
      }
      
      selectedImages.value.push(...files);
      errorMessage.value = '';
      
      // Limpiar el input para permitir seleccionar el mismo archivo de nuevo
      event.target.value = '';
    };

    const handleVideoSelect = (event) => {
      const files = Array.from(event.target.files);
      const availableSlots = 4 - totalFiles.value;
      
      if (files.length > availableSlots) {
        errorMessage.value = `Solo puedes agregar ${availableSlots} archivos más (máximo 4 total)`;
        return;
      }
      
      selectedVideos.value.push(...files);
      errorMessage.value = '';
      
      // Limpiar el input
      event.target.value = '';
    };

    const removeImage = (index) => {
      selectedImages.value.splice(index, 1);
      errorMessage.value = '';
    };

    const removeVideo = (index) => {
      selectedVideos.value.splice(index, 1);
      errorMessage.value = '';
    };

    const getFilePreview = (file) => {
      return URL.createObjectURL(file);
    };

    const submitPost = async () => {
      if (!canPost.value) return;

      const token = localStorage.getItem('authToken');
      const userNickname = localStorage.getItem('userNickname');
      const userId = localStorage.getItem('userId');
      
      if (!token || !userNickname) {
        errorMessage.value = 'Error de autenticación. Inicia sesión nuevamente.';
        return;
      }

      if (!userId) {
        errorMessage.value = 'Error: ID de usuario no encontrado. Inicia sesión nuevamente.';
        return;
      }

      try {
        isPosting.value = true;
        errorMessage.value = '';

        // Crear FormData para enviar el post
        const formData = new FormData();
        formData.append('contenido', postContent.value.trim());
        formData.append('usuarioId', userId);
        // Si es comentario, enviar postPadreId
        if (props.postPadreId) {
          formData.append('postPadreId', props.postPadreId);
        }
        // Enviar fecha de creación local (ISO)
        formData.append('fechaCreacion', new Date().toISOString());

        // Agregar imágenes
        selectedImages.value.forEach(image => {
          formData.append('imagenes', image);
        });

        // Agregar videos
        selectedVideos.value.forEach(video => {
          formData.append('videos', video);
        });

        const newPost = await postService.createPost(formData, token);
        
        // Limpiar formulario
        postContent.value = '';
        selectedImages.value = [];
        selectedVideos.value = [];
        
        // Emitir evento de post creado
        emit('postCreated', newPost);
        
      } catch (error) {
        console.error('Error creating post:', error);
        errorMessage.value = error.message;
      } finally {
        isPosting.value = false;
      }
    };

    return {
      postContent,
      selectedImages,
      selectedVideos,
      isPosting,
      errorMessage,
      imageInput,
      videoInput,
      charCount,
      totalFiles,
      canPost,
      updateCharCount,
      handleImageSelect,
      handleVideoSelect,
      removeImage,
      removeVideo,
      getFilePreview,
      submitPost
    };
  }
};
</script>

<style scoped>
.create-post {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.post-input {
  position: relative;
}

.content-textarea {
  width: 100%;
  min-height: 120px;
  padding: 1rem;
  border: 2px solid #e1e8ed;
  border-radius: 12px;
  font-size: 16px;
  font-family: inherit;
  resize: vertical;
  background: #fafafa;
  transition: border-color 0.2s, background-color 0.2s;
}

.content-textarea:focus {
  outline: none;
  border-color: #6a0dad;
  background: white;
}

.char-counter {
  position: absolute;
  bottom: 8px;
  right: 12px;
  font-size: 12px;
  color: #666;
  background: rgba(255, 255, 255, 0.9);
  padding: 2px 6px;
  border-radius: 4px;
}

.char-counter.near-limit {
  color: #ff9500;
}

.char-counter.at-limit {
  color: #ff4444;
  font-weight: bold;
}

.media-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.media-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.file-inputs {
  display: flex;
  gap: 1rem;
}

.file-input-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  color: #495057;
  transition: all 0.2s;
}

.file-input-label:hover {
  background: #e9ecef;
  border-color: #6a0dad;
}

.file-counter {
  font-size: 12px;
  color: #666;
  font-weight: 500;
}

.selected-files {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
}

.file-previews {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 1rem;
}

.file-preview {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  background: white;
  border: 1px solid #dee2e6;
}

.file-preview img,
.file-preview video {
  width: 100%;
  height: 80px;
  object-fit: cover;
  display: block;
}

.remove-file {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 20px;
  height: 20px;
  background: rgba(255, 68, 68, 0.9);
  color: white;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  font-size: 12px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.remove-file:hover {
  background: #ff4444;
}

.file-name {
  display: block;
  padding: 4px 8px;
  font-size: 11px;
  color: #666;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  background: #f8f9fa;
}

.post-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.btn-post {
  background-color: #6a0dad;
  color: white;
  border: none;
  border-radius: 25px;
  padding: 0.75rem 2rem;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-post:hover:not(:disabled) {
  background-color: #8a2be2;
  transform: translateY(-1px);
}

.btn-post:disabled {
  background-color: #ccc;
  cursor: not-allowed;
  transform: none;
}

.error-message {
  color: #ff4444;
  font-size: 14px;
  font-weight: 500;
}

/* Responsive */
@media (max-width: 768px) {
  .file-inputs {
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .media-controls {
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
  }
  
  .file-previews {
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  }
}
</style>
